import { ReactNode } from 'react';
import { useIntl } from 'react-intl';

import { ContentWrapper } from '@/components/b2b/shared/styles';
import { useAnalytics } from '@/hooks/analytics';
import { useDeviceState } from '@/hooks/store';
import { useComponentDidMount } from '@/hooks/utils/useComponentDidMount';
import genericMessages from '@/messages/messages';
import { CALM_ONE_LINKS } from '@/utils/deeplinks/deeplinks';

import MainTextContentCtaColumn from '../MainTextContentCtaColumn';
import messages from './messages';
import { Grid, SorryButton, SorryText, UnderlinedLink } from './styles';

export const getOpenCalmAppLink = ({
	isMobile,
	isProduction,
}: {
	isMobile: boolean | undefined;
	isProduction: boolean;
}): string => {
	if (!isMobile) {
		return CALM_ONE_LINKS.login;
	}
	return isProduction ? 'calm://upsell' : 'calm-dev://upsell';
};

const Sorry = ({ isRevampRedempFlow }: { isRevampRedempFlow?: boolean }) => {
	const { logEvent } = useAnalytics();
	const { formatMessage } = useIntl();
	const device = useDeviceState();

	useComponentDidMount(() => {
		logEvent({ eventName: 'Sorry : Landed' });
	});

	function onCtaClick(): void {
		const url = getOpenCalmAppLink({
			isMobile: device.isMobile,
			isProduction: process.env.NEXT_PUBLIC_CALM_ENV === 'production',
		});
		logEvent({
			eventName: 'Sorry : CTA : Clicked',
			eventProps: {
				url,
			},
		});
		window.location.assign(url);
	}

	if (isRevampRedempFlow) {
		return (
			<ContentWrapper isRevampRedempFlow={isRevampRedempFlow}>
				<MainTextContentCtaColumn
					title={formatMessage(messages.sorryTitle)}
					buttonProps={{
						buttonText: formatMessage(messages.openCalmButton),
						buttonLink: CALM_ONE_LINKS.app,
					}}
					isAppStoreButtonsVisible={false}
					style={{ textAlign: 'left' }}
				>
					<SorryText isRevampRedempFlow>
						{formatMessage(messages.cancelToRedeemRevamp, {
							cancelationinstructions: (...chunks) => (
								<UnderlinedLink href="https://support.calm.com/hc/en-us/articles/115002473607">
									{chunks}
								</UnderlinedLink>
							),
							p: (...chunks: ReactNode[]) => <p>{chunks}</p>,
							bold: (...chunks: ReactNode[]) => <b>{chunks}</b>,
							ul: (...chunks: ReactNode[]) => <ul>{chunks}</ul>,
							ol: (...chunks: ReactNode[]) => <ol>{chunks}</ol>,
							li: (...chunks: ReactNode[]) => <li>{chunks}</li>,
						})}
					</SorryText>
				</MainTextContentCtaColumn>
			</ContentWrapper>
		);
	}

	return (
		<Grid allowOverflow>
			<ContentWrapper>
				<SorryText>
					{formatMessage(messages.cancelToRedeem, {
						cancelationinstructions: (...chunks) => (
							<UnderlinedLink href="https://support.calm.com/hc/en-us/articles/115002473607">
								{chunks}
							</UnderlinedLink>
						),
						p: (...chunks: ReactNode[]) => <p>{chunks}</p>,
						bold: (...chunks: ReactNode[]) => <b>{chunks}</b>,
						ul: (...chunks: ReactNode[]) => <ul>{chunks}</ul>,
						ol: (...chunks: ReactNode[]) => <ol>{chunks}</ol>,
						li: (...chunks: ReactNode[]) => <li>{chunks}</li>,
					})}
				</SorryText>
				<SorryButton onPress={onCtaClick}>{formatMessage(genericMessages.openCalmApp)}</SorryButton>
			</ContentWrapper>
		</Grid>
	);
};

export default Sorry;
